/**
 * RWBR — MONEY, AND THE LEDGER IT MOVES THROUGH.
 *
 * This file owns two things: the unit, and the substrate.
 *
 * THE UNIT (D-009). Everything under the hood is Megabucks, because that is
 * what Sagliano's tables are printed in and rescaling them would mean rewriting
 * every one. One Megabuck is ten thousand dollars (SAG/14, verbatim: "The
 * amounts below are in Megabucks. 1 Megabuck (Mb) = $10,000"). Every number a
 * player ever sees is rendered in dollars — career winnings reads "$4.2M", not
 * "420 Mb" — and that conversion happens here and nowhere else.
 *
 * THE SUBSTRATE. A balance is a FOLD over an append-only event stream. It is
 * never a running total kept alongside the stream, and there is no `balance`
 * field anywhere in this codebase that can be written to.
 *
 *   This is not a stylistic preference. `Season Points 3.xlsx` — the user's own
 *   workbook, and the practical evidence this project was handed — keeps season
 *   totals in cells that are maintained in parallel with the per-race rows, and
 *   the two disagree. The standings module already refuses that shape
 *   (`foldSeason`); money is the place where the same bug would be most
 *   expensive and least visible, because a wrong dollar figure still looks like
 *   a dollar figure. So: one stream, one fold, no shortcuts.
 *
 * AUDITABILITY. A MoneyEvent carries the same evidence a RuleEvent does — the
 * dice that produced it, every modifier named and valued, the page it came
 * from, and a sentence a human can read. "Team collects 104 Mb" is not an
 * acceptable ledger line; "Track purse 137 + TV money 554 = 691 winnings;
 * winner's 25% share is 173; team's 60% of that is 104 (SAG/23)" is.
 *
 * SIGN CONVENTION. `amountMb` is signed and always read from the perspective of
 * `account`. Positive is money arriving. A driver's salary is +4.44 on his own
 * book and −4.44 on his team's, and both events exist, because Sagliano books
 * it twice on purpose (SAG/26 income, SAG/31 expense) and a league where the
 * two are the same row cannot show a team going broke paying a star.
 *
 * ROUNDING. Nothing is rounded during accumulation. Sagliano's worked examples
 * round for the page — he writes 43 where the arithmetic gives 43.19 — and if
 * we rounded at every step the errors would compound over 36 races into real
 * money. Full precision in the ledger; rounding is a presentation concern and
 * lives in the formatters at the bottom of this file.
 */

import type { DiceRecord, Modifier } from '../ruleEvent.ts';

// ---------------------------------------------------------------------------
// The unit
// ---------------------------------------------------------------------------

/** SAG/14, verbatim: "1 Megabuck (Mb) = $10,000". */
export const DOLLARS_PER_MEGABUCK = 10_000;

export const CITE_MEGABUCK = 'SAG/14 — "The amounts below are in Megabucks. 1 Megabuck (Mb) = $10,000"';
export const CITE_CURRENCY_POLICY = 'D-009 — dollars on screen, Megabucks under the hood';

/** Megabucks → dollars. */
export function toDollars(mb: number): number {
  return mb * DOLLARS_PER_MEGABUCK;
}

/** Dollars → Megabucks. */
export function toMegabucks(dollars: number): number {
  return dollars / DOLLARS_PER_MEGABUCK;
}

// ---------------------------------------------------------------------------
// Who holds money
// ---------------------------------------------------------------------------

/**
 * SAG/12 abstracts the League, the TV networks and the track owners — "Most
 * activities and revenues and expenses of the League, TV Networks, and Track
 * Owners are abstracted (or ignored) in these rules since they are not the
 * focus of RWBR." We keep books for them anyway, because they cost nothing to
 * maintain and they let the ledger prove it balances: every Megabuck a driver
 * or team loses to a fine or an entry fee has somewhere to be.
 */
export type AccountKind = 'driver' | 'team' | 'league' | 'track';

export interface MoneyAccount {
  kind: AccountKind;
  /** Driver id, team id, or a fixed handle for the league and track books. */
  id: string;
}

export const LEAGUE_ACCOUNT: MoneyAccount = { kind: 'league', id: 'league' };

export function trackAccount(trackId: string): MoneyAccount {
  return { kind: 'track', id: trackId };
}

export function driverAccount(driverId: string): MoneyAccount {
  return { kind: 'driver', id: driverId };
}

export function teamAccount(teamId: string): MoneyAccount {
  return { kind: 'team', id: teamId };
}

/** Stable string form, used as the fold's map key. */
export function accountKey(a: MoneyAccount): string {
  return `${a.kind}:${a.id}`;
}

// ---------------------------------------------------------------------------
// What money is for
// ---------------------------------------------------------------------------

/**
 * Every category Sagliano's two big sections name, plus the two book-keeping
 * ones the abstraction needs.
 *
 * `winnings` and `contingency` together are CAREER WINNINGS in the sense a
 * record book means it — the money a driver won on the racetrack, as distinct
 * from what he was paid to show up. That distinction is the whole reason the
 * user asked for this system ("I would love to add a career dollar winnings
 * portion to it to track career winnings for drivers"), so it is a property of
 * the category set rather than something computed by a caller who might get it
 * wrong.
 */
export type MoneyCategory =
  // income
  | 'winnings'
  | 'contingency'
  | 'sponsorship-primary'
  | 'sponsorship-major'
  | 'sponsorship-associate'
  | 'automaker-support'
  | 'family-wealth'
  | 'royalties'
  | 'salary'
  | 'bonus'
  // expense
  | 'race-cost'
  | 'research-development'
  | 'testing'
  | 'repairs'
  | 'car-replacement'
  | 'fine'
  // abstracted books
  | 'tv-money'
  | 'admissions'
  | 'purse-contribution';

/** The categories that count toward a driver's career winnings. */
export const CAREER_WINNINGS_CATEGORIES: readonly MoneyCategory[] = ['winnings', 'contingency'];

export type MoneyPhase = 'preseason' | 'race-week' | 'race' | 'post-race' | 'off-season';

// ---------------------------------------------------------------------------
// The envelope
// ---------------------------------------------------------------------------

/**
 * One movement of money. Complete on its own: an auditor handed a single
 * MoneyEvent can say what happened, why, on whose authority, and off which
 * dice, without reading anything else.
 */
export interface MoneyEvent {
  seasonId: string;
  /** Null for anything outside a race weekend — the off-season, pre-season. */
  raceNumber: number | null;
  phase: MoneyPhase;
  account: MoneyAccount;
  /** Signed, in Megabucks, from `account`'s point of view. Never rounded. */
  amountMb: number;
  category: MoneyCategory;
  /** Readable aloud. The booth and the balance sheet share this string. */
  description: string;
  ruleCitation: string;
  /** Every roll that produced this figure, in order. Empty for fixed costs. */
  dice: DiceRecord[];
  /** Every named, valued adjustment — the arithmetic, shown. */
  modifiers: Modifier[];
  /**
   * The other side of the transaction, where there is one. Salary is +4.44 to a
   * driver and −4.44 to his team; both events carry the other's account here so
   * the pair can be shown as one line in a statement.
   */
  counterparty?: MoneyAccount;
}

/** Build a MoneyEvent with the boilerplate defaulted. */
export function moneyEvent(
  e: Omit<MoneyEvent, 'dice' | 'modifiers'> & { dice?: DiceRecord[]; modifiers?: Modifier[] },
): MoneyEvent {
  return { dice: [], modifiers: [], ...e };
}

/**
 * The two halves of a transfer, as a pair. Used wherever one party's expense is
 * another's income — salary, fines, the purse.
 */
export function transfer(args: {
  seasonId: string;
  raceNumber: number | null;
  phase: MoneyPhase;
  from: MoneyAccount;
  to: MoneyAccount;
  amountMb: number;
  category: MoneyCategory;
  description: string;
  ruleCitation: string;
  dice?: DiceRecord[];
  modifiers?: Modifier[];
}): [MoneyEvent, MoneyEvent] {
  const { from, to, amountMb, ...rest } = args;
  return [
    moneyEvent({ ...rest, account: to, amountMb, counterparty: from }),
    moneyEvent({ ...rest, account: from, amountMb: -amountMb, counterparty: to }),
  ];
}

// ---------------------------------------------------------------------------
// The fold
// ---------------------------------------------------------------------------

export interface AccountBook {
  account: MoneyAccount;
  key: string;
  /** Everything in, everything out. */
  balanceMb: number;
  /** Positive movements only. */
  incomeMb: number;
  /** Negative movements only, reported positive. */
  expenseMb: number;
  /** Signed net by category. */
  byCategory: Record<string, number>;
  /** Signed net by race number. Off-season entries land under key `0`. */
  byRace: Record<number, number>;
  /**
   * `winnings` + `contingency`, income only. What a record book calls winnings:
   * money won racing, not money paid to show up.
   */
  careerWinningsMb: number;
  eventCount: number;
}

export interface MoneyLedger {
  books: Record<string, AccountBook>;
  /** Every account that saw a movement, in first-seen order. */
  accounts: MoneyAccount[];
  /** The number of events folded. */
  eventCount: number;
  /**
   * Sum of every `amountMb` in the stream. A stream built only from `transfer`
   * pairs sums to exactly zero; one that also mints money from the abstracted
   * outside world (sponsorship, automaker support, family wealth) will not, and
   * that is correct — see `assertBalanced` for the strict form.
   */
  netMb: number;
}

function emptyBook(account: MoneyAccount): AccountBook {
  return {
    account,
    key: accountKey(account),
    balanceMb: 0,
    incomeMb: 0,
    expenseMb: 0,
    byCategory: {},
    byRace: {},
    careerWinningsMb: 0,
    eventCount: 0,
  };
}

/**
 * THE ONLY WAY TO KNOW WHAT ANYONE HAS.
 *
 * Pure, total, and order-independent for the sums (order is preserved only in
 * `accounts`, for display). Call it on the whole stream, or on a slice of it —
 * `foldMoney(events.filter(e => e.raceNumber <= 12))` is a mid-season balance
 * sheet and needs no separate code path.
 */
export function foldMoney(events: readonly MoneyEvent[]): MoneyLedger {
  const books: Record<string, AccountBook> = {};
  const accounts: MoneyAccount[] = [];
  let netMb = 0;

  for (const e of events) {
    const key = accountKey(e.account);
    let book = books[key];
    if (!book) {
      book = books[key] = emptyBook(e.account);
      accounts.push(e.account);
    }

    book.balanceMb += e.amountMb;
    if (e.amountMb >= 0) book.incomeMb += e.amountMb;
    else book.expenseMb += -e.amountMb;

    book.byCategory[e.category] = (book.byCategory[e.category] ?? 0) + e.amountMb;
    const raceKey = e.raceNumber ?? 0;
    book.byRace[raceKey] = (book.byRace[raceKey] ?? 0) + e.amountMb;

    if (e.amountMb > 0 && (CAREER_WINNINGS_CATEGORIES as readonly string[]).includes(e.category)) {
      book.careerWinningsMb += e.amountMb;
    }

    book.eventCount += 1;
    netMb += e.amountMb;
  }

  return { books, accounts, eventCount: events.length, netMb };
}

/** A book by account, or an empty one — never undefined. */
export function bookFor(ledger: MoneyLedger, account: MoneyAccount): AccountBook {
  return ledger.books[accountKey(account)] ?? emptyBook(account);
}

/** Career winnings for one driver, straight off the stream. */
export function careerWinnings(events: readonly MoneyEvent[], driverId: string): number {
  let total = 0;
  for (const e of events) {
    if (e.account.kind !== 'driver' || e.account.id !== driverId) continue;
    if (e.amountMb <= 0) continue;
    if (!(CAREER_WINNINGS_CATEGORIES as readonly string[]).includes(e.category)) continue;
    total += e.amountMb;
  }
  return total;
}

/**
 * Every driver's career winnings, richest first. The leaderboard the user asked
 * for, computed from the stream rather than stored.
 */
export function careerWinningsTable(
  events: readonly MoneyEvent[],
): { driverId: string; winningsMb: number }[] {
  const totals: Record<string, number> = {};
  for (const e of events) {
    if (e.account.kind !== 'driver' || e.amountMb <= 0) continue;
    if (!(CAREER_WINNINGS_CATEGORIES as readonly string[]).includes(e.category)) continue;
    totals[e.account.id] = (totals[e.account.id] ?? 0) + e.amountMb;
  }
  return Object.entries(totals)
    .map(([driverId, winningsMb]) => ({ driverId, winningsMb }))
    .sort((a, b) => b.winningsMb - a.winningsMb || a.driverId.localeCompare(b.driverId));
}

/**
 * Strict double-entry check over the subset of the stream that is supposed to
 * balance — everything with a `counterparty`. Money that enters the world from
 * outside it (a sponsor's cheque, an automaker's support, an owner's own
 * fortune) has no counterparty by design, and is excluded.
 *
 * Returns the residual, which must be 0 within floating-point tolerance.
 */
export function transferResidual(events: readonly MoneyEvent[]): number {
  let residual = 0;
  for (const e of events) if (e.counterparty) residual += e.amountMb;
  return residual;
}

// ---------------------------------------------------------------------------
// Presentation (D-009)
// ---------------------------------------------------------------------------

/**
 * Dollars, the way a broadcast graphic writes them. $6,400,000 becomes "$6.4M";
 * small change keeps its cents so a 0.5 Mb contingency award reads "$5,000"
 * rather than "$0.0M".
 */
export function formatDollars(mb: number, opts: { sign?: boolean } = {}): string {
  const dollars = toDollars(mb);
  const neg = dollars < 0;
  const abs = Math.abs(dollars);

  // Pick the unit off the ROUNDED figure, not the raw one: $999,999 rounds to
  // "1000K", which is a million written the long way (audit, 4 Oct 2026).
  const rounds = (n: number): number => Math.round(n * 100) / 100;
  let body: string;
  if (rounds(abs / 1_000_000) >= 1000) body = `$${trim(abs / 1_000_000_000)}B`;
  else if (rounds(abs / 1000) >= 1000) body = `$${trim(abs / 1_000_000)}M`;
  else if (abs >= 100_000) body = `$${trim(abs / 1000)}K`;
  else body = `$${Math.round(abs).toLocaleString('en-US')}`;

  if (neg) return `-${body}`;
  return opts.sign && dollars > 0 ? `+${body}` : body;
}

/** Megabucks, the way the rulebook writes them: "36 Mb", "2.5 Mb". */
export function formatMegabucks(mb: number): string {
  return `${trim(mb)} Mb`;
}

/** One or two decimals, whichever the number actually needs. */
function trim(n: number): string {
  const rounded = Math.round(n * 100) / 100;
  if (Number.isInteger(rounded)) return String(rounded);
  if (Math.round(rounded * 10) / 10 === rounded) return rounded.toFixed(1);
  return rounded.toFixed(2);
}

/** Both units, for the ledger view where the source table is in Mb. */
export function formatBoth(mb: number): string {
  return `${formatDollars(mb)} (${formatMegabucks(mb)})`;
}
